import { ActionIcon as ActionIconMantine, ActionIconProps } from "@mantine/core";
import { forwardRef } from "react";
import styled from "styled-components";

import { Color } from "src/theme";
import { Tooltip } from "../Tooltip";
import { DELAY_FOR_ICON_TOOLTIP } from "./constants";

type StyledActionIconProps = Omit<ActionIconProps, "color"> & {
    color?: Color;
    className?: string;
    tooltipWithinPortal?: boolean;
};

export const ActionIcon = forwardRef<
    HTMLButtonElement,
    Omit<StyledActionIconProps, "component"> & { tooltip?: string; onClick?: () => void }
>(({ tooltip, children, tooltipWithinPortal = true, disabled, ...props }, ref) => {
    const button = (
        <StyledActionIcon ref={ref} disabled={disabled} {...props}>
            {children}
        </StyledActionIcon>
    );

    // Wrap in span when disabled to allow tooltip to show (disabled elements don't trigger mouse events)
    const wrappedButton = disabled ? <span style={{ cursor: "not-allowed" }}>{button}</span> : button;

    return tooltip ? (
        <Tooltip
            backgroundColor={props.color ?? Color.neutral200}
            color={Color.supportGraphite500}
            withinPortal={tooltipWithinPortal}
            openDelay={DELAY_FOR_ICON_TOOLTIP}
            label={tooltip}
        >
            {wrappedButton}
        </Tooltip>
    ) : (
        wrappedButton
    );
});

const StyledActionIcon = styled(ActionIconMantine).withConfig({
    shouldForwardProp: (prop) => !["color"].includes(prop as string),
})<StyledActionIconProps>`
    &:disabled {
        background-color: transparent;
        border-color: transparent;
    }

    &:hover:enabled {
        background-color: ${({ color }) => color || "transparent"};
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.color.neutral100};
    }
`;
